import { memo, type ReactNode } from "react";
import Svg, { Circle, Ellipse, G, Path, Rect } from "react-native-svg";

import {
  AVATAR_EYE_HEX,
  AVATAR_HAIR_HEX,
  AVATAR_SKIN_HEX,
  AVATAR_TOP_HEX,
  type AvatarBrowStyle,
  type AvatarExpression,
  type AvatarEyeStyle,
  type AvatarFaceShape,
  type AvatarHairStyle,
  type AvatarNoseStyle,
  type UserAvatarConfig,
} from "@/lib/avatar-config";

/**
 * Lightweight illustrated avatar rendered as inline SVG on a 100×100 grid.
 * No network, no image assets — friendly, readable at small sizes, and aligned
 * to the opncue blue/white/navy brand. Memoized for list performance.
 *
 * Draws the person only. The pin/vibe corner badge is an overlay drawn by
 * `ui/avatar.tsx`, because this SVG is rendered inside a circular crop and a
 * badge in here could only ever sit over the face or be sliced by the mask —
 * it could never straddle the rim the way the presence dot does.
 */

/** Mix a hex color toward white by `amount` (0..1). */
function lighten(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `#${((1 << 24) + (mix(r) << 16) + (mix(g) << 8) + mix(b)).toString(16).slice(1)}`;
}

/** Mix a hex color toward black by `amount` (0..1). */
function darken(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const mix = (c: number) => Math.round(c * (1 - amount));
  return `#${((1 << 24) + (mix(r) << 16) + (mix(g) << 8) + mix(b)).toString(16).slice(1)}`;
}

/** Head geometry per face shape (centered near 50,46). */
function Head({ shape, skin }: { shape: AvatarFaceShape; skin: string }) {
  const stroke = darken(skin, 0.14);
  const sw = 1;
  switch (shape) {
    case "oval":
      return <Ellipse cx={50} cy={46} rx={16} ry={20} fill={skin} stroke={stroke} strokeWidth={sw} />;
    case "square":
      return <Rect x={33} y={29} width={34} height={36} rx={6} fill={skin} stroke={stroke} strokeWidth={sw} />;
    case "rectangle":
      return <Rect x={35} y={24} width={30} height={45} rx={9} fill={skin} stroke={stroke} strokeWidth={sw} />;
    case "heart":
      return (
        <Path
          d="M50 69 C37 60 33 49 34 38 C35 29 41 25 50 26 C59 25 65 29 66 38 C67 49 63 60 50 69 Z"
          fill={skin}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
    case "diamond":
      return (
        <Path
          d="M50 25 C61 28 69 40 66 50 C63 60 56 67 50 69 C44 67 37 60 34 50 C31 40 39 28 50 25 Z"
          fill={skin}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
    case "triangle":
      return (
        <Path
          d="M50 28 C61 29 66 39 66 49 C66 59 60 67 50 69 C40 67 34 59 34 49 C34 39 39 29 50 28 Z"
          fill={skin}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
    case "round":
    default:
      return <Circle cx={50} cy={46} r={18} fill={skin} stroke={stroke} strokeWidth={sw} />;
  }
}

type HairArtwork = {
  back?: ReactNode;
  front?: ReactNode;
  foreground?: ReactNode;
};

/**
 * Small open spiral strokes laid over a cluster of curl circles — the circles
 * give the mass its silhouette, these give it the ringlet texture (the flat
 * circles alone read as bubbles, not curls). Every other curl gets a hook,
 * mostly in the highlight tone with an occasional dark one for depth.
 */
function curlRinglets(
  curls: readonly (readonly [number, number, number])[],
  highlight: string,
  lowlight: string,
) {
  return curls.map(([cx, cy, r], i) => {
    if (i % 2) return null;
    const a = r * 0.45;
    const sweep = i % 4 === 0 ? 1 : 0;
    return (
      <Path
        key={`ringlet-${i}`}
        d={`M${cx - a * 0.9} ${cy + a * 0.6} a${a} ${a} 0 1 ${sweep} ${a * 1.4} ${-a * 1.1}`}
        stroke={i % 6 === 0 ? lowlight : highlight}
        strokeWidth={Math.max(0.9, r * 0.26)}
        strokeLinecap="round"
        fill="none"
        opacity={0.55}
      />
    );
  });
}

function strandPath(
  key: string,
  d: string,
  color: string,
  width: number,
  opacity = 1,
) {
  return (
    <Path
      key={key}
      d={d}
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
      fill="none"
      opacity={opacity}
    />
  );
}

function Hair({ style, color }: { style: AvatarHairStyle; color: string }): HairArtwork {
  if (style === "none") return {};

  const c = color;
  const shade = darken(c, 0.22);
  const softShade = darken(c, 0.12);
  const shine = lighten(c, 0.2);
  switch (style) {
    case "buzz_cut":
      return {
        front: (
          <G>
            <Path d="M32 40 C32 28 41 25 50 25 C59 25 68 28 68 40 C61 35 39 35 32 40 Z" fill={c} />
            <Path d="M34 39 C40 35 60 35 66 39" stroke={shade} strokeWidth={1.2} strokeLinecap="round" fill="none" opacity={0.55} />
          </G>
        ),
      };
    case "short_straight":
      return {
        back: <Path d="M29 40 C29 25 40 21 50 21 C60 21 71 25 71 40 C71 52 68 62 63 66 L57 66 C61 54 60 45 57 39 C53 35 47 35 43 39 C40 45 39 54 43 66 L37 66 C32 62 29 52 29 40 Z" fill={c} />,
        front: (
          <G>
            <Path d="M31 42 C31 27 41 23 50 23 C59 23 69 27 69 42 C63 34 57 31 50 31 C43 31 37 34 31 42 Z" fill={c} />
            <Path d="M37 43 C38 51 37 58 35 64" stroke={shade} strokeWidth={2} strokeLinecap="round" fill="none" opacity={0.42} />
            <Path d="M63 43 C62 51 63 58 65 64" stroke={shade} strokeWidth={2} strokeLinecap="round" fill="none" opacity={0.42} />
            <Path d="M37 36 C43 31 50 31 63 36" stroke={shine} strokeWidth={1.35} strokeLinecap="round" fill="none" opacity={0.42} />
          </G>
        ),
      };
    case "cropped":
      return {
        front: (
          <G>
            <Path d="M32 42 C32 28 41 24 50 24 C59 24 68 28 68 42 C62 36 57 34 50 34 C43 34 38 36 32 42 Z" fill={c} />
            <Path d="M36 35 C42 31 58 31 64 35" stroke={shade} strokeWidth={1.8} strokeLinecap="round" fill="none" opacity={0.45} />
          </G>
        ),
      };
    case "fade":
      return {
        front: (
          <G>
            <Path d="M32 42 C32 28 41 24 50 24 C59 24 68 28 68 42 C62 35 57 33 50 33 C43 33 38 35 32 42 Z" fill={c} />
            <Path d="M34 41 C38 36 42 34 47 33" stroke={shade} strokeWidth={2} strokeLinecap="round" fill="none" opacity={0.45} />
            <Path d="M66 41 C62 36 58 34 53 33" stroke={shade} strokeWidth={2} strokeLinecap="round" fill="none" opacity={0.45} />
          </G>
        ),
      };
    case "side_part":
      return {
        front: (
          <G>
            <Path d="M31 43 C31 27 41 23 50 23 C60 23 69 28 69 43 C63 34 57 31 48 32 C43 31 37 35 31 43 Z" fill={c} />
            <Path d="M46 27 C45 31 44 34 42 38" stroke={shade} strokeWidth={1.5} strokeLinecap="round" fill="none" />
            <Path d="M47 29 C53 29 61 32 66 38" stroke={shine} strokeWidth={1.2} strokeLinecap="round" fill="none" opacity={0.45} />
          </G>
        ),
      };
    case "short_curls": {
      // A crown, not a mask: the face runs y≈28–64 (brows 38, eyes 46), so no
      // curl may reach below y≈38. Rows hug the scalp arc from temple to
      // temple; the lowest row is the fringe, sitting just above the brows.
      const curls = [
        // top row
        [40, 23, 3.8],
        [45, 21.5, 4],
        [50, 21, 4.1],
        [55, 21.5, 4],
        [60, 23, 3.8],
        // upper-side row
        [34.5, 28, 3.6],
        [39, 25.5, 3.8],
        [44, 24.5, 3.9],
        [50, 24, 4],
        [56, 24.5, 3.9],
        [61, 25.5, 3.8],
        [65.5, 28, 3.6],
        // mid row
        [32, 33, 3.3],
        [37, 30, 3.6],
        [42, 29, 3.7],
        [47, 28.5, 3.8],
        [53, 28.5, 3.8],
        [58, 29, 3.7],
        [63, 30, 3.6],
        [68, 33, 3.3],
        // fringe — the bottom edge of the hair
        [35, 35.5, 3],
        [40, 33.5, 3.2],
        [45, 32.5, 3.3],
        [50, 32, 3.4],
        [55, 32.5, 3.3],
        [60, 33.5, 3.2],
        [65, 35.5, 3],
        // sideburns
        [30.5, 39, 2.5],
        [69.5, 39, 2.5],
      ] as const;
      return {
        front: (
          <G>
            <Path d="M30 43 C29 34 31 27 37 23 C41 20 46 19 50 19 C54 19 59 20 63 23 C69 27 71 34 70 43 C66 38 62 36 57 35 C52 34 48 34 43 35 C38 36 34 38 30 43 Z" fill={c} />
            {curls.map(([cx, cy, r], i) => (
              <Circle
                key={`curl-${i}`}
                cx={cx}
                cy={cy}
                r={r}
                fill={i % 5 === 0 || i % 7 === 0 ? softShade : c}
              />
            ))}
            {curlRinglets(curls, shine, shade)}
          </G>
        ),
      };
    }
    case "medium_curls": {
      // Same crown rule as short_curls (nothing over the face; brows at 38),
      // just with more volume: taller on top, wider at the temples, and a
      // back-layer halo that drops to ear level beside the head — never the
      // full-face mane this once was.
      const backCurls = [
        [29, 44, 5.5],
        [27.5, 37, 5.5],
        [29, 30, 5.5],
        [33, 24, 5.5],
        [39, 19.5, 5.5],
        [46, 17, 5.5],
        [54, 17, 5.5],
        [61, 19.5, 5.5],
        [67, 24, 5.5],
        [71, 30, 5.5],
        [72.5, 37, 5.5],
        [71, 44, 5.5],
        [30, 50, 5],
        [70, 50, 5],
      ] as const;
      const crownCurls = [
        // top row
        [39, 21, 4.5],
        [44, 19, 4.7],
        [50, 18.5, 4.8],
        [56, 19, 4.7],
        [61, 21, 4.5],
        // second row
        [33, 27, 4.2],
        [38, 24, 4.4],
        [44, 22.5, 4.6],
        [50, 22, 4.7],
        [56, 22.5, 4.6],
        [62, 24, 4.4],
        [67, 27, 4.2],
        // third row
        [30.5, 32, 4],
        [36, 29, 4.2],
        [41, 27.5, 4.3],
        [46, 27, 4.4],
        [54, 27, 4.4],
        [59, 27.5, 4.3],
        [64, 29, 4.2],
        [69.5, 32, 4],
        // fringe — bottom edge of the hair, above the brows
        [34, 34.5, 3.6],
        [39, 32.5, 3.8],
        [44, 31.5, 3.9],
        [50, 31, 4],
        [56, 31.5, 3.9],
        [61, 32.5, 3.8],
        [66, 34.5, 3.6],
        // side volume, over the ears at the head's edge
        [29.5, 38, 3.4],
        [28.5, 43, 3.2],
        [70.5, 38, 3.4],
        [71.5, 43, 3.2],
      ] as const;
      return {
        back: (
          <G>
            <Circle cx={50} cy={33} r={22} fill={softShade} />
            {backCurls.map(([cx, cy, r], i) => (
              <Circle
                key={`back-curl-${i}`}
                cx={cx}
                cy={cy}
                r={r}
                fill={i % 2 ? shade : softShade}
              />
            ))}
          </G>
        ),
        front: (
          <G>
            <Path d="M29 45 C26 33 31 24 38 20 C42 17 46 16 50 16 C54 16 58 17 62 20 C69 24 74 33 71 45 C67 39 63 36 58 35 C53 33 47 33 42 35 C37 36 33 39 29 45 Z" fill={c} />
            {crownCurls.map(([cx, cy, r], i) => (
              <Circle
                key={`medium-curl-${i}`}
                cx={cx}
                cy={cy}
                r={r}
                fill={i % 4 === 0 || i % 6 === 0 ? softShade : c}
              />
            ))}
            {curlRinglets(crownCurls, shine, shade)}
          </G>
        ),
      };
    }
    case "wavy":
      return {
        front: (
          <G>
            <Path d="M30 45 C30 27 42 22 50 22 C58 22 70 27 70 45 C66 39 62 43 58 38 C54 43 50 38 46 43 C42 38 36 42 30 45 Z" fill={c} />
            <Path d="M36 36 C42 31 47 38 53 33 C59 29 63 34 66 38" stroke={shine} strokeWidth={1.3} strokeLinecap="round" fill="none" opacity={0.42} />
          </G>
        ),
      };
    case "long":
      return {
        back: <Path d="M28 40 C28 25 40 21 50 21 C60 21 72 25 72 40 C72 58 68 68 62 74 L56 74 C61 58 60 46 57 39 C53 35 47 35 43 39 C40 46 39 58 44 74 L38 74 C32 68 28 58 28 40 Z" fill={c} />,
        front: (
          <G>
            <Path d="M31 42 C31 27 41 23 50 23 C59 23 69 27 69 42 C63 34 57 31 50 31 C43 31 37 34 31 42 Z" fill={c} />
            <Path d="M38 43 C39 55 38 64 35 71" stroke={shade} strokeWidth={2.2} strokeLinecap="round" fill="none" opacity={0.45} />
            <Path d="M62 43 C61 55 62 64 65 71" stroke={shade} strokeWidth={2.2} strokeLinecap="round" fill="none" opacity={0.45} />
          </G>
        ),
      };
    case "bun":
      return {
        back: <Circle cx={50} cy={22} r={8.2} fill={softShade} />,
        front: (
          <G>
            <Path d="M32 43 C32 29 41 25 50 25 C59 25 68 29 68 43 C62 35 56 32 50 32 C44 32 38 35 32 43 Z" fill={c} />
            <Path d="M38 35 Q50 25 62 35" stroke={shine} strokeWidth={1.5} strokeLinecap="round" fill="none" opacity={0.45} />
            <Path d="M37 39 Q50 28 63 39" stroke={shade} strokeWidth={1.2} strokeLinecap="round" fill="none" opacity={0.45} />
          </G>
        ),
      };
    case "short_locs": {
      const locs = [
        ["l1", "M36 31 C34 35 34 39 35 43", 4.1],
        ["l2", "M42 28 C40 33 40 38 41 43", 4.3],
        ["l3", "M48 27 C47 32 47 37 48 42", 4.5],
        ["l4", "M54 27 C55 32 55 37 54 42", 4.5],
        ["l5", "M60 28 C62 33 62 38 61 43", 4.3],
        ["l6", "M64 32 C67 36 67 40 65 44", 4.1],
      ] as const;
      return {
        front: (
          <G>
            <Path d="M32 42 C32 28 41 23 50 23 C59 23 68 28 68 42 C63 36 57 33 50 33 C43 33 37 36 32 42 Z" fill={softShade} />
            {locs.map(([key, d, width], i) => strandPath(key, d, i % 2 ? c : shade, width))}
            <Path d="M36 30 C43 25 56 25 64 30" stroke={shine} strokeWidth={1.2} strokeLinecap="round" fill="none" opacity={0.35} />
          </G>
        ),
      };
    }
    case "long_locs": {
      const backLocs = [
        ["lb1", "M34 31 C30 43 31 58 35 70"],
        ["lb2", "M41 27 C37 42 38 58 40 73"],
        ["lb3", "M59 27 C63 42 62 58 60 73"],
        ["lb4", "M66 31 C70 43 69 58 65 70"],
      ] as const;
      return {
        back: (
          <G>
            {backLocs.map(([key, d], i) => strandPath(key, d, i % 2 ? c : shade, 5.2))}
          </G>
        ),
        front: (
          <G>
            <Path d="M31 42 C31 27 41 23 50 23 C59 23 69 27 69 42 C63 35 57 32 50 32 C43 32 37 35 31 42 Z" fill={softShade} />
            {strandPath("lf1", "M43 28 C41 36 40 45 40 53", c, 4.4)}
            {strandPath("lf2", "M57 28 C59 36 60 45 60 53", shade, 4.4)}
            <Path d="M38 31 C45 26 55 26 62 31" stroke={shine} strokeWidth={1.1} strokeLinecap="round" fill="none" opacity={0.35} />
          </G>
        ),
      };
    }
    case "box_braids": {
      const sideBraids = [
        ["bb1", "M34 33 C30 44 31 58 35 72"],
        ["bb2", "M41 31 C38 45 38 59 41 74"],
        ["bb3", "M59 31 C62 45 62 59 59 74"],
        ["bb4", "M66 33 C70 44 69 58 65 72"],
      ] as const;
      return {
        back: (
          <G>
            {sideBraids.map(([key, d], i) => strandPath(key, d, i % 2 ? c : shade, 4.2))}
          </G>
        ),
        front: (
          <G>
            <Path d="M31 42 C31 28 40 23 50 23 C60 23 69 28 69 42 C63 35 57 32 50 32 C43 32 37 35 31 42 Z" fill={c} />
            <Path d="M42 27 L39 35 M50 25 L50 34 M58 27 L61 35" stroke={shine} strokeWidth={1.1} strokeLinecap="round" fill="none" opacity={0.5} />
            {strandPath("bf1", "M39 34 C37 41 37 48 38 55", shade, 3.4)}
            {strandPath("bf2", "M61 34 C63 41 63 48 62 55", shade, 3.4)}
          </G>
        ),
      };
    }
    default:
      return {};
  }
}

/** Mouth per expression. */
function Mouth({ expression, skin }: { expression: AvatarExpression; skin: string }) {
  const line = darken(skin, 0.45);
  switch (expression) {
    case "smile":
      return <Path d="M43 54 Q50 61 57 54" stroke={line} strokeWidth={2} fill="none" strokeLinecap="round" />;
    case "cheerful":
      return <Path d="M42 53 Q50 63 58 53 Q50 57 42 53 Z" fill={line} />;
    case "calm":
      return <Path d="M44 55 Q50 58 56 55" stroke={line} strokeWidth={2} fill="none" strokeLinecap="round" />;
    case "neutral":
      return <Path d="M44 56 L56 56" stroke={line} strokeWidth={2} fill="none" strokeLinecap="round" />;
    case "soft_smile":
    default:
      return <Path d="M44 55 Q50 59 56 55" stroke={line} strokeWidth={2} fill="none" strokeLinecap="round" />;
  }
}

/** A single eye centered at (cx, 46). */
function Eye({
  cx,
  style,
  iris,
  sleepyClosed,
}: {
  cx: number;
  style: AvatarEyeStyle;
  iris: string;
  sleepyClosed: boolean;
}) {
  const white = "#FFFFFF";
  const lid = "#3A2E2A";
  if (sleepyClosed) {
    // Cheerful / sleepy: a happy closed arc.
    return (
      <Path
        d={`M${cx - 3.4} 47 Q${cx} 44 ${cx + 3.4} 47`}
        stroke="#2B2B2B"
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
      />
    );
  }
  // Per-style sclera geometry.
  const geom: Record<AvatarEyeStyle, { rx: number; ry: number; tilt?: number }> = {
    round: { rx: 3.6, ry: 3.6 },
    almond: { rx: 4, ry: 3 },
    wide: { rx: 4.6, ry: 3.4 },
    narrow: { rx: 4, ry: 2.3 },
    upturned: { rx: 4, ry: 3, tilt: cx < 50 ? 12 : -12 },
    sleepy: { rx: 4, ry: 2.4 },
  };
  const g = geom[style];
  return (
    // Use the SVG transform string (works on native + web); the `rotation`/
    // `origin` props leak an invalid `transform-origin` DOM attr on web.
    <G transform={`rotate(${g.tilt ?? 0}, ${cx}, 46)`}>
      <Ellipse cx={cx} cy={46} rx={g.rx} ry={g.ry} fill={white} />
      <Circle cx={cx} cy={46} r={Math.min(g.ry, 2.5)} fill={iris} />
      <Circle cx={cx} cy={46} r={1.15} fill="#161616" />
      <Circle cx={cx - 0.7} cy={45.2} r={0.5} fill="#FFFFFF" />
      {/* Upper lid line for definition */}
      <Path
        d={`M${cx - g.rx} ${46 - g.ry + 0.3} Q${cx} ${46 - g.ry - 1.2} ${cx + g.rx} ${46 - g.ry + 0.3}`}
        stroke={lid}
        strokeWidth={1}
        fill="none"
        strokeLinecap="round"
      />
      {style === "sleepy" ? (
        <Path
          d={`M${cx - g.rx} ${46 - 0.4} Q${cx} ${46 - 2} ${cx + g.rx} ${46 - 0.4}`}
          stroke={lid}
          strokeWidth={0.9}
          fill="none"
          opacity={0.5}
        />
      ) : null}
    </G>
  );
}

/** A single eyebrow above the eye at (cx, ~40). */
function Brow({ cx, style, color }: { cx: number; style: AvatarBrowStyle; color: string }) {
  const mirror = cx < 50 ? 1 : -1;
  const stroke = darken(color, 0.1);
  switch (style) {
    case "straight":
      return <Path d={`M${cx - 3.5} 40 L${cx + 3.5} 40`} stroke={stroke} strokeWidth={1.8} strokeLinecap="round" fill="none" />;
    case "arched":
      return <Path d={`M${cx - 3.6} 40.4 Q${cx} 37.4 ${cx + 3.6} 40.4`} stroke={stroke} strokeWidth={1.8} strokeLinecap="round" fill="none" />;
    case "thick":
      return <Path d={`M${cx - 3.8} 39.6 Q${cx} 37.8 ${cx + 3.8} 39.8`} stroke={stroke} strokeWidth={3.2} strokeLinecap="round" fill="none" />;
    case "thin":
      return <Path d={`M${cx - 3.4} 40 Q${cx} 38.8 ${cx + 3.4} 40`} stroke={stroke} strokeWidth={1} strokeLinecap="round" fill="none" />;
    case "rounded":
      return <Path d={`M${cx - 3.4} 40.6 Q${cx} 37.8 ${cx + 3.4} 40.6`} stroke={stroke} strokeWidth={2.2} strokeLinecap="round" fill="none" />;
    case "soft":
    default:
      return <Path d={`M${cx - 3.4} 40 Q${cx + mirror * 0.6} 38.4 ${cx + 3.4} 40`} stroke={stroke} strokeWidth={2} strokeLinecap="round" fill="none" />;
  }
}

/** Nose centered near (50, 51). */
function Nose({ style, skin }: { style: AvatarNoseStyle; skin: string }) {
  const stroke = darken(skin, 0.22);
  const shade = darken(skin, 0.1);
  switch (style) {
    case "round":
      return <Ellipse cx={50} cy={51} rx={2.4} ry={2} fill={shade} opacity={0.6} />;
    case "narrow":
      return <Path d="M50 45 L49 51 Q50 52.4 51 51" stroke={stroke} strokeWidth={1.2} fill="none" strokeLinecap="round" strokeLinejoin="round" />;
    case "wide":
      return <Path d="M47.6 51 Q50 53.2 52.4 51" stroke={stroke} strokeWidth={1.4} fill="none" strokeLinecap="round" />;
    case "button":
      return <Ellipse cx={50} cy={51} rx={1.9} ry={1.6} fill={shade} opacity={0.7} />;
    case "soft":
    default:
      return <Path d="M48.8 51 Q50 52.6 51.2 51" stroke={stroke} strokeWidth={1.2} fill="none" strokeLinecap="round" />;
  }
}

type Props = {
  config: UserAvatarConfig;
  size: number;
};

function IllustratedAvatarBase({ config, size }: Props) {
  const skin = AVATAR_SKIN_HEX[config.skinTone];
  const hair = AVATAR_HAIR_HEX[config.hairColor];
  const top = AVATAR_TOP_HEX[config.topColor];
  const bg = lighten(top, 0.82);
  const irisColor = AVATAR_EYE_HEX[config.eyeColor];
  const cheerful = config.expression === "cheerful";
  const showEars = config.headCovering !== "headscarf" && config.headCovering !== "wrap";
  const hairLayers = Hair({ style: config.hairStyle, color: hair });


  return (
    <Svg width={size} height={size} viewBox="0 0 100 100">
      {/* Background badge */}
      <Circle cx={50} cy={50} r={50} fill={bg} />

      {/* Shoulders / top */}
      <Path d="M18 100 C18 82 33 74 50 74 C67 74 82 82 82 100 Z" fill={top} />
      {config.topStyle === "hoodie" ? (
        <Path d="M40 76 Q50 84 60 76 L60 86 Q50 92 40 86 Z" fill={darken(top, 0.16)} />
      ) : config.topStyle === "collared" ? (
        <Path d="M42 75 L50 84 L58 75 L54 74 L50 80 L46 74 Z" fill={lighten(top, 0.35)} />
      ) : config.topStyle === "jacket" ? (
        <Path d="M48 75 L48 96 M52 75 L52 96" stroke={darken(top, 0.22)} strokeWidth={2} />
      ) : null}
      {config.accessory === "scarf" ? (
        <Path d="M36 74 Q50 82 64 74 L64 80 Q50 88 36 80 Z" fill={darken(top, 0.28)} />
      ) : null}

      {/* Hair back layer: long styles sit behind head/ears/facial features. */}
      {hairLayers.back}

      {/* Neck */}
      <Rect x={44} y={62} width={12} height={12} rx={4} fill={darken(skin, 0.08)} />

      {/* Ears + optional earrings */}
      {showEars ? (
        <G>
          <Ellipse cx={32} cy={47} rx={4} ry={5.5} fill={skin} />
          <Ellipse cx={68} cy={47} rx={4} ry={5.5} fill={skin} />
          {config.accessory === "earrings" ? (
            <G fill="#E7B008">
              <Circle cx={32} cy={53} r={2.1} />
              <Circle cx={68} cy={53} r={2.1} />
            </G>
          ) : null}
        </G>
      ) : null}

      {/* Head */}
      <Head shape={config.faceShape} skin={skin} />

      {/* Hair front layer: scalp, hairline, top volume, and safe front detail. */}
      {hairLayers.front}

      {/* Eyebrows */}
      <Brow cx={44} style={config.browStyle} color={hair} />
      <Brow cx={56} style={config.browStyle} color={hair} />

      {/* Eyes */}
      <Eye cx={44} style={config.eyeStyle} iris={irisColor} sleepyClosed={cheerful} />
      <Eye cx={56} style={config.eyeStyle} iris={irisColor} sleepyClosed={cheerful} />

      {/* Nose */}
      <Nose style={config.noseStyle} skin={skin} />

      {/* Glasses */}
      {config.glasses !== "none" ? (
        <G
          stroke={config.glasses === "bold" ? "#222" : "#3A3A3A"}
          strokeWidth={config.glasses === "bold" ? 2.4 : config.glasses === "thin" ? 1 : 1.6}
          fill="none"
        >
          {config.glasses === "round" ? (
            <>
              <Circle cx={44} cy={46} r={5.5} />
              <Circle cx={56} cy={46} r={5.5} />
            </>
          ) : (
            <>
              <Rect x={38.5} y={41.5} width={11} height={9} rx={config.glasses === "square" ? 1.5 : 3} />
              <Rect x={50.5} y={41.5} width={11} height={9} rx={config.glasses === "square" ? 1.5 : 3} />
            </>
          )}
          <Path d="M49.5 46 L50.5 46" />
        </G>
      ) : null}

      {/* Reserved for intentional, sparse strands; never used for selection. */}
      {hairLayers.foreground}

      {/* Facial hair */}
      {config.facialHair !== "none" ? (
        <G fill={hair}>
          {config.facialHair === "stubble" ? (
            <Path d="M36 52 Q50 66 64 52 Q60 60 50 62 Q40 60 36 52 Z" fill={hair} opacity={0.35} />
          ) : config.facialHair === "mustache" ? (
            <Path d="M43 53 Q50 57 57 53 Q50 51 43 53 Z" />
          ) : config.facialHair === "goatee" ? (
            <Path d="M46 56 Q50 64 54 56 Q50 60 46 56 Z" />
          ) : config.facialHair === "beard_short" ? (
            <Path d="M35 50 Q50 68 65 50 Q62 62 50 64 Q38 62 35 50 Z" opacity={0.9} />
          ) : (
            <Path d="M33 46 Q50 74 67 46 Q66 64 50 66 Q34 64 33 46 Z" />
          )}
        </G>
      ) : null}

      {/* Mouth */}
      <Mouth expression={config.expression} skin={skin} />

      {/* Head covering (over hair) */}
      {config.headCovering !== "none" ? (
        <G>
          {config.headCovering === "beanie" ? (
            <G>
              <Path d="M30 40 C30 24 42 22 50 22 C58 22 70 24 70 40 Z" fill={darken(top, 0.1)} />
              <Rect x={30} y={38} width={40} height={5} rx={2.5} fill={lighten(top, 0.2)} />
            </G>
          ) : config.headCovering === "cap" ? (
            <G>
              <Path d="M31 40 C31 26 42 23 50 23 C58 23 69 26 69 40 C60 34 40 34 31 40 Z" fill={darken(top, 0.12)} />
              <Path d="M31 40 Q22 41 20 44 L44 43 Z" fill={darken(top, 0.2)} />
            </G>
          ) : config.headCovering === "headscarf" ? (
            <Path d="M28 46 C28 24 42 22 50 22 C58 22 72 24 72 46 C72 58 66 64 60 68 L58 60 C64 54 64 40 58 34 C50 30 44 32 40 38 C36 46 36 58 42 66 L40 68 C34 62 28 58 28 46 Z" fill={darken(top, 0.08)} />
          ) : config.headCovering === "turban" ? (
            <G fill={darken(top, 0.1)}>
              <Path d="M30 40 C30 22 42 20 50 20 C58 20 70 22 70 40 C60 30 40 30 30 40 Z" />
              <Path d="M30 40 Q50 30 70 40 Q50 46 30 40 Z" fill={lighten(top, 0.18)} />
            </G>
          ) : (
            <Path d="M29 44 C29 24 42 21 50 21 C58 21 71 24 71 44 C62 33 38 33 29 44 Z" fill={darken(top, 0.1)} />
          )}
        </G>
      ) : null}

      {/* Accessory: headphones / pin */}
      {config.accessory === "headphones" ? (
        <G>
          <Path d="M30 44 C30 26 42 22 50 22 C58 22 70 26 70 44" stroke="#2C3E50" strokeWidth={3} fill="none" />
          <Rect x={26.5} y={42} width={7} height={12} rx={3} fill="#2C3E50" />
          <Rect x={66.5} y={42} width={7} height={12} rx={3} fill="#2C3E50" />
        </G>
      ) : config.accessory === "pin" ? (
        <Circle cx={62} cy={30} r={3} fill="#E5484D" />
      ) : null}

    </Svg>
  );
}

export const IllustratedAvatar = memo(IllustratedAvatarBase);
